<template>
	<Floater offsetDistance="0" :arrow="false">
		<button class="btn btn-sm flex items-center gap-2">
			<component :is="current.icon" :size="16" />
			<ChevronDown :size="12" />
		</button>
		<template #content="{ close }">
			<ul class="menu menu-compact p-1">
				<li v-for="t in themes" :key="t.value">
					<a
						@click="
							() => {
								setTheme(t.value)
								close()
							}
						"
						class="flex items-center gap-2">
						<component :is="t.icon" :size="14" />
						{{ t.label }}
					</a>
				</li>
			</ul>
		</template>
	</Floater>
</template>

<script setup lang="ts">
	import { computed, onBeforeUnmount, ref, watch } from 'vue'
	import type { Ref } from 'vue'
	import { Eclipse, Sun, Moon, SunMoon, ChevronDown } from 'lucide-vue-next'
	import Floater from '@/components/common/Floater.vue'
	import { useI18n } from 'vue-i18n'
	import { _translations } from './_translations'

	declare global {
		interface Window {
			__revotecTheme?: Ref<ThemePreference>
		}
	}

	type ThemeName = 'revotec-light' | 'revotec-dim' | 'revotec-dark'
	type ThemePreference = ThemeName | 'system'
	const storageKey = 'theme'

	const getSystemTheme = (): ThemeName =>
		window.matchMedia('(prefers-color-scheme: dark)').matches ? 'revotec-dim' : 'revotec-light'

	const systemTheme = ref<ThemeName>(getSystemTheme())
	const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
	const handleSystemChange = (event: MediaQueryListEvent) => {
		systemTheme.value = event.matches ? 'revotec-dim' : 'revotec-light'
	}
	mediaQuery.addEventListener('change', handleSystemChange)
	onBeforeUnmount(() => mediaQuery.removeEventListener('change', handleSystemChange))

	const themePreference = (() => {
		if (window.__revotecTheme) {
			return window.__revotecTheme
		}
		const initial = (localStorage.getItem(storageKey) as ThemePreference | null) || 'system'
		const th = ref<ThemePreference>(initial)
		watch(th, (val) => localStorage.setItem(storageKey, val))
		window.__revotecTheme = th
		return th
	})()

	const resolvedTheme = computed<ThemeName>(() =>
		themePreference.value === 'system' ? systemTheme.value : themePreference.value
	)

	function applyTheme(val: ThemeName) {
		document.documentElement.setAttribute('data-theme', val)
		const isDark = val === 'revotec-dark' || val === 'revotec-dim'
		document.documentElement.classList.toggle('dark', isDark)
	}

	watch(resolvedTheme, (val) => applyTheme(val), { immediate: true })

	// ─── Component logic ──────────────────────────────────────────
	const { t } = useI18n(_translations)

	const themes = computed(() => [
		{ label: t('layout.theme.system'), value: 'system', icon: SunMoon },
		{ label: t('layout.theme.light'), value: 'revotec-light', icon: Sun },
		// { label: 'Light', value: 'revotec-contrast', icon: Sun },
		{ label: t('layout.theme.dim'), value: 'revotec-dim', icon: Eclipse },
		{ label: t('layout.theme.dark'), value: 'revotec-dark', icon: Moon },
	])
	const current = computed(() => themes.value.find((t) => t.value === themePreference.value) || themes.value[0])
	function setTheme(val: ThemePreference) {
		themePreference.value = val
	}
</script>
